import React, { useEffect, useState } from "react";
import { DateRange } from "react-date-range";
import { format, subDays } from "date-fns";
import { toast } from "react-toastify";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

// Dummy Loader (replace with your component if needed)
const LoadingSpinner = () => (
  <div className="text-white text-lg">Loading...</div>
);

const Dashboard = () => {
  const [activeView, setActiveView] = useState("purchase");

  // ================= PURCHASE STATE =================
  const [data, setData] = useState([]);
  const [showPicker, setShowPicker] = useState(false);
  const [range, setRange] = useState([
    {
      startDate: subDays(new Date(), 7),
      endDate: new Date(),
      key: "selection",
    },
  ]);

  // ================= SUBUSER STATE =================
  const [loading, setLoading] = useState(false);
  const [serverData, setServerData] = useState([]);

  // ================= FETCH PURCHASE =================
  const fetchPurchaseData = async () => {
    const fromdate = format(range[0].startDate, "yyyy-MM-dd");
    const todate = format(range[0].endDate, "yyyy-MM-dd");
    const token = localStorage.getItem("token");

    try {
      const res = await fetch(
        `https://esimgo.clay.in/api/ESIMGOAdmin/Get_purchasereport_sourcewise?flag=2&fromdate=${fromdate}&todate=${todate}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const json = await res.json();
      if (json.isSuccess) {
        setData(json.partnersale || []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  // ================= FETCH SUBUSER =================
  const fetchSubAccounts = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `https://esimgo.clay.in/api/Renew/GetPartnerSubAccounts`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            id: 0,
            partnerCode: "",
            pageSize: 100,
            pageIndex: 1,
          }),
        }
      );

      const data = await response.json();

      if (data.isSuccess) {
        setServerData(data.records || []);
      } else {
        toast.error("Failed to fetch records");
      }
    } catch (err) {
      toast.error("Server error");
    } finally {
      setLoading(false);
    }
  };

  // ================= INITIAL LOAD =================
  useEffect(() => {
    fetchPurchaseData();
    fetchSubAccounts();
  }, []);

  // ================= HANDLERS =================
  const handleApply = () => {
    fetchPurchaseData();
    setShowPicker(false);
  };

  // ================= UI =================
  return (
    <div className="p-6">
      {/* Toggle Buttons */}
      <div className="flex gap-4 mb-6">
        <button
          onClick={() => setActiveView("purchase")}
          className={`px-4 py-2 rounded ${
            activeView === "purchase"
              ? "bg-blue-600 text-white"
              : "bg-gray-200"
          }`}
        >
          📊 Purchase History
        </button>

        <button
          onClick={() => setActiveView("subuser")}
          className={`px-4 py-2 rounded ${
            activeView === "subuser"
              ? "bg-blue-600 text-white"
              : "bg-gray-200"
          }`}
        >
          👤 Sub Users
        </button>
      </div>

      {/* ================= PURCHASE VIEW ================= */}
      {activeView === "purchase" && (
        <div>
          {/* Date Filter */}
          <div className="mb-4">
            <button
              onClick={() => setShowPicker(!showPicker)}
              className="bg-blue-500 text-white px-4 py-2 rounded"
            >
              Select Date Range
            </button>

            {showPicker && (
              <div className="mt-4 bg-white p-4 shadow rounded">
                <DateRange
                  editableDateInputs
                  onChange={(item) => setRange([item.selection])}
                  moveRangeOnFirstSelection={false}
                  ranges={range}
                />

                <button
                  onClick={handleApply}
                  className="mt-2 bg-green-500 text-white px-4 py-2 rounded"
                >
                  Apply
                </button>
              </div>
            )}
          </div>

          {/* Table */}
          <div className="overflow-auto">
            <table className="min-w-full border">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-2 border">Partner</th>
                  <th className="p-2 border">Company</th>
                  <th className="p-2 border">Plan</th>
                  <th className="p-2 border">Amount</th>
                  <th className="p-2 border">Date</th>
                  <th className="p-2 border">Sub User</th>
                </tr>
              </thead>
              <tbody>
                {data.map(
                  (item, index) =>
                    item.subUserCode && (
                      <tr key={index} className="text-center">
                        <td className="p-2 border">{item.partnername}</td>
                        <td className="p-2 border">{item.companyname}</td>
                        <td className="p-2 border">{item.planname}</td>
                        <td className="p-2 border">
                          ${item.purchaseamount}
                        </td>
                        <td className="p-2 border">
                          {item.purchasedate}
                        </td>
                        <td className="p-2 border">
                          {item.subUserCode}
                        </td>
                      </tr>
                    )
                )}
              </tbody>
            </table>

            {data.length === 0 && (
              <p className="text-center mt-4 text-gray-500">
                No data found
              </p>
            )}
          </div>
        </div>
      )}

      {/* ================= SUBUSER VIEW ================= */}
      {activeView === "subuser" && (
        <div className="overflow-x-auto">
          {loading && (
            <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-50">
              <LoadingSpinner />
            </div>
          )}

          <table className="w-full bg-white shadow rounded">
            <thead className="bg-gray-200">
              <tr>
                <th className="p-2 border">S.NO</th>
                <th className="p-2 border">Username</th>
                <th className="p-2 border">Email</th>
                <th className="p-2 border">Number</th>
                <th className="p-2 border">Role</th>
                <th className="p-2 border">Status</th>
                <th className="p-2 border">Country</th>
                <th className="p-2 border">Parent Email</th>
              </tr>
            </thead>

            <tbody>
              {serverData.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center h-40 font-bold">
                    No Active Sub user account
                  </td>
                </tr>
              ) : (
                serverData.map((row, index) => (
                  <tr key={row.id} className="text-center border-b">
                     <td className="p-2 border">{index + 1}</td>
                     <td className="p-2 border">{row.userName}</td>
                     <td className="p-2 border">{row.email}</td>
                     <td className="p-2 border">{row.phoneNumber}</td>
                     <td className="p-2 border">{row.role}</td>
                     <td className="p-2 border">{row.status}</td>
                     <td className="p-2 border">{row.country}</td>
                     <td className="p-2 border">{row.parentemail}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Dashboard;